class BBCSiteHeader extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.desktopQuery = window.matchMedia('(min-width: 1100px)');
    this.desktopItems = [...this.querySelectorAll('[data-bbc-mega-item]')];
    this.desktopTriggers = [...this.querySelectorAll('[data-bbc-mega-trigger]')];
    this.drawer = this.querySelector('[data-bbc-drawer]');
    this.drawerOverlay = this.querySelector('[data-bbc-drawer-overlay]');
    this.drawerOpenButton = this.querySelector('[data-bbc-drawer-open]');
    this.drawerCloseButton = this.querySelector('[data-bbc-drawer-close]');
    this.mobileTriggers = [...this.querySelectorAll('[data-bbc-mobile-trigger]')];
    this.closeTimers = new Map();
    this.lastFocused = null;

    this.bindDesktopMenu();
    this.bindMobileDrawer();
    this.bindGlobalEvents();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    document.body.classList.remove('bbc-menu-lock');
  }

  bindDesktopMenu() {
    this.desktopItems.forEach((item) => {
      const trigger = item.querySelector('[data-bbc-mega-trigger]');
      if (!trigger) return;

      trigger.addEventListener('click', () => {
        if (!this.desktopQuery.matches) return;
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (isOpen && trigger.dataset.openSource !== 'click') {
          trigger.dataset.openSource = 'click';
          return;
        }
        isOpen ? this.closeDesktopItem(item) : this.openDesktopItem(item, 'click');
      }, { signal: this.signal });

      item.addEventListener('pointerenter', () => {
        if (!this.desktopQuery.matches) return;
        this.clearCloseTimer(item);
        this.openDesktopItem(item, 'hover');
      }, { signal: this.signal });

      item.addEventListener('pointerleave', () => {
        if (!this.desktopQuery.matches) return;
        this.scheduleClose(item);
      }, { signal: this.signal });

      item.addEventListener('focusin', () => {
        if (!this.desktopQuery.matches) return;
        this.clearCloseTimer(item);
        this.openDesktopItem(item, 'focus');
      }, { signal: this.signal });

      item.addEventListener('focusout', (event) => {
        if (!this.desktopQuery.matches || item.contains(event.relatedTarget)) return;
        this.scheduleClose(item, 80);
      }, { signal: this.signal });
    });
  }

  openDesktopItem(item, source = 'click') {
    const trigger = item.querySelector('[data-bbc-mega-trigger]');
    const panel = item.querySelector('[data-bbc-mega-panel]');
    if (!trigger || !panel) return;

    this.closeAllDesktop(item);
    this.clearCloseTimer(item);
    if (trigger.getAttribute('aria-expanded') !== 'true') trigger.dataset.openSource = source;
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    window.requestAnimationFrame(() => panel.setAttribute('data-open', 'true'));
  }

  closeDesktopItem(item, immediate = false) {
    const trigger = item.querySelector('[data-bbc-mega-trigger]');
    const panel = item.querySelector('[data-bbc-mega-panel]');
    if (!trigger || !panel) return;

    this.clearCloseTimer(item);
    trigger.setAttribute('aria-expanded', 'false');
    delete trigger.dataset.openSource;
    panel.removeAttribute('data-open');

    const hidePanel = () => {
      if (trigger.getAttribute('aria-expanded') === 'false') panel.hidden = true;
    };

    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      hidePanel();
    } else {
      window.setTimeout(hidePanel, 180);
    }
  }

  closeAllDesktop(exceptItem = null, immediate = false) {
    this.desktopItems.forEach((item) => {
      if (item !== exceptItem) this.closeDesktopItem(item, immediate);
    });
  }

  scheduleClose(item, delay = 150) {
    this.clearCloseTimer(item);
    this.closeTimers.set(item, window.setTimeout(() => this.closeDesktopItem(item), delay));
  }

  clearCloseTimer(item) {
    if (!this.closeTimers.has(item)) return;
    window.clearTimeout(this.closeTimers.get(item));
    this.closeTimers.delete(item);
  }

  bindMobileDrawer() {
    this.drawerOpenButton?.addEventListener('click', () => this.openDrawer(), { signal: this.signal });
    this.drawerCloseButton?.addEventListener('click', () => this.closeDrawer(), { signal: this.signal });
    this.drawerOverlay?.addEventListener('click', () => this.closeDrawer(), { signal: this.signal });

    this.mobileTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.toggleMobilePanel(trigger), { signal: this.signal });
    });

    this.drawer?.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this.closeDrawer(false), { signal: this.signal });
    });
  }

  openDrawer() {
    if (!this.drawer || !this.drawerOverlay || !this.drawerOpenButton) return;
    this.closeAllDesktop(null, true);
    this.lastFocused = document.activeElement;
    this.drawer.inert = false;
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.setAttribute('data-open', 'true');
    this.drawerOverlay.setAttribute('data-open', 'true');
    this.drawerOpenButton.setAttribute('aria-expanded', 'true');
    document.body.classList.add('bbc-menu-lock');
    window.requestAnimationFrame(() => this.drawerCloseButton?.focus());
  }

  closeDrawer(restoreFocus = true) {
    if (!this.drawer || !this.drawerOverlay || !this.drawerOpenButton) return;
    this.drawer.removeAttribute('data-open');
    this.drawerOverlay.removeAttribute('data-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.inert = true;
    this.drawerOpenButton.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('bbc-menu-lock');
    if (restoreFocus && this.lastFocused instanceof HTMLElement) this.lastFocused.focus();
  }

  toggleMobilePanel(trigger) {
    const panelId = trigger.getAttribute('aria-controls');
    const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
    if (!panel) return;
    const willOpen = trigger.getAttribute('aria-expanded') !== 'true';

    this.mobileTriggers.forEach((otherTrigger) => {
      const otherId = otherTrigger.getAttribute('aria-controls');
      const otherPanel = otherId ? this.querySelector(`#${CSS.escape(otherId)}`) : null;
      otherTrigger.setAttribute('aria-expanded', 'false');
      if (otherPanel) otherPanel.hidden = true;
    });

    if (willOpen) {
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
    }
  }

  bindGlobalEvents() {
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.closeAllDesktop();
    }, { signal: this.signal });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        const drawerIsOpen = this.drawer?.getAttribute('aria-hidden') === 'false';
        if (drawerIsOpen) {
          this.closeDrawer();
        } else {
          const openTrigger = this.desktopTriggers.find((trigger) => trigger.getAttribute('aria-expanded') === 'true');
          this.closeAllDesktop();
          openTrigger?.focus();
        }
      }

      if (event.key === 'Tab' && this.drawer?.getAttribute('aria-hidden') === 'false') {
        this.trapDrawerFocus(event);
      }
    }, { signal: this.signal });

    this.desktopQuery.addEventListener('change', () => {
      this.closeAllDesktop(null, true);
      this.closeDrawer(false);
    }, { signal: this.signal });
  }

  trapDrawerFocus(event) {
    const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('bbc-site-header')) {
  customElements.define('bbc-site-header', BBCSiteHeader);
}
